<template>
  <div class="user-edit-page">
    <div class="page-header mb-5">
      <div class="level">
        <div class="level-left">
          <div class="level-item">
            <h1 class="title is-3">Edit User</h1>
          </div>
        </div>
        <div class="level-right">
          <div class="level-item">
            <permission-router-link to="/users" class="button is-light">
              <span class="icon">
                <i class="bx bx-arrow-back"></i>
              </span>
              <span>Back to Users</span>
            </permission-router-link>
          </div>
        </div>
      </div>
    </div>

    <div v-if="loading" class="has-text-centered py-5">
      <div class="is-flex is-justify-content-center mb-4">
        <span class="icon is-large has-text-primary">
          <i class="bx bx-loader bx-spin bx-lg"></i>
        </span>
      </div>
      <p class="has-text-grey">Loading user information...</p>
    </div>

    <div v-else class="columns">
      <div class="column is-8 is-offset-2">
        <div class="card">
          <header class="card-header has-background-primary">
            <p class="card-header-title has-text-white">Edit User: {{ form.first_name }} {{ form.last_name }}</p>
          </header>
          <div class="card-content">
            <form @submit.prevent="updateUser">
              <div class="columns">
                <div class="column is-4">
                  <div class="field">
                    <label class="label">First Name*</label>
                    <div class="control">
                      <input 
                        v-model="form.first_name" 
                        class="input" 
                        type="text" 
                        placeholder="Enter first name" 
                        required
                      >
                    </div>
                    <p v-if="errors.first_name" class="help is-danger">{{ errors.first_name }}</p>
                  </div>
                </div>
                <div class="column is-4">
                  <div class="field">
                    <label class="label">Middle Name</label>
                    <div class="control">
                      <input 
                        v-model="form.middle_name" 
                        class="input" 
                        type="text" 
                        placeholder="Enter middle name"
                      >
                    </div>
                    <p v-if="errors.middle_name" class="help is-danger">{{ errors.middle_name }}</p>
                  </div>
                </div>
                <div class="column is-4">
                  <div class="field">
                    <label class="label">Last Name*</label>
                    <div class="control">
                      <input 
                        v-model="form.last_name" 
                        class="input" 
                        type="text" 
                        placeholder="Enter last name" 
                        required
                      >
                    </div>
                    <p v-if="errors.last_name" class="help is-danger">{{ errors.last_name }}</p>
                  </div>
                </div>
              </div>

              <div class="columns">
                <div class="column is-6">
                  <div class="field">
                    <label class="label">Email*</label>
                    <div class="control has-icons-left">
                      <input 
                        v-model="form.email" 
                        class="input" 
                        type="email" 
                        placeholder="Enter email address" 
                        required
                      >
                      <span class="icon is-small is-left">
                        <i class="bx bx-envelope"></i>
                      </span>
                    </div>
                    <p v-if="errors.email" class="help is-danger">{{ errors.email }}</p>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field">
                    <label class="label">Phone</label>
                    <div class="control has-icons-left">
                      <input 
                        v-model="form.phone" 
                        class="input" 
                        type="tel" 
                        placeholder="Enter phone number"
                      >
                      <span class="icon is-small is-left">
                        <i class="bx bx-phone"></i>
                      </span>
                    </div>
                    <p v-if="errors.phone" class="help is-danger">{{ errors.phone }}</p>
                  </div>
                </div>
              </div>


              <div class="field">
                <label class="label">Address</label>
                <div class="control">
                  <input 
                    v-model="form.address" 
                    class="input" 
                    type="text" 
                    placeholder="Enter address"
                  >
                </div>
                <p v-if="errors.address" class="help is-danger">{{ errors.address }}</p>
              </div>

              <div class="columns">
                <div class="column is-6">
                  <div class="field">
                    <label class="label">City</label>
                    <div class="control">
                      <input 
                        v-model="form.city" 
                        class="input" 
                        type="text" 
                        placeholder="Enter city"
                      >
                    </div>
                    <p v-if="errors.city" class="help is-danger">{{ errors.city }}</p>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field">
                    <label class="label">Zip Code</label>
                    <div class="control">
                      <input 
                        v-model="form.zip_code" 
                        class="input" 
                        type="text" 
                        placeholder="Enter zip code"
                      >
                    </div>
                    <p v-if="errors.zip_code" class="help is-danger">{{ errors.zip_code }}</p>
                  </div>
                </div>
              </div>



              <div class="field">
                <label class="label">Role*</label>
                <div v-if="rolesLoading" class="has-text-centered py-3">
                  <span class="icon is-medium has-text-primary">
                    <i class="bx bx-loader bx-spin"></i>
                  </span>
                  <p class="has-text-grey">Loading roles...</p>
                </div>
                <div v-else-if="roles.length === 0" class="notification is-light is-warning">
                  <p>No roles available. Please create roles first.</p>
                </div>
                <div v-else class="control">
                  <div class="select is-fullwidth">
                    <select v-model="form.role_id" required>
                      <option value="" disabled>Select a role</option>
                      <option v-for="role in roles" :key="role.id" :value="role.id">{{ role.name }}</option>
                    </select>
                  </div>
                  <p v-if="selectedRole" class="help has-text-grey mt-2">{{ selectedRole.description }}</p>
                </div>
              </div>

              <div class="columns">
                <div class="column is-6">
                  <div class="field">
                    <label class="checkbox">
                      <input type="checkbox" v-model="form.is_active">
                      Active Account
                    </label>
                    <p class="help">If unchecked, user will not be able to login</p>
                  </div>
                </div>

              </div>

              <div class="field mt-5">
                <div class="control">
                  <button 
                    type="submit" 
                    class="button is-primary is-fullwidth" 
                    :class="{'is-loading': saving}"
                  >
                    Update User
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import axios from 'axios'
import { useRouter, useRoute } from 'vue-router'

export default {
  name: 'UserEdit',
  setup() {
    const router = useRouter()
    const route = useRoute()
    const userId = route.params.id

    const user = ref(null)

    const roles = ref([])
    const loading = ref(true)
    const rolesLoading = ref(true)

    const saving = ref(false)
    const errors = ref({})

    const form = ref({
      first_name: '',
      middle_name: '',
      last_name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      zip_code: '',
      is_superadmin: false,
      is_active: true,
      organization_id: '',
      role_id: ''
    })

    const selectedRole = computed(() => {
      if (!form.value.role_id) return null
      return roles.value.find(role => role.id === form.value.role_id)
    })

    const fetchUser = async () => {
      loading.value = true
      try {
        const response = await axios.get(`/api/users/${userId}`)
        user.value = response.data

        // Initialize form with user data
        form.value.first_name = user.value.first_name || ''
        form.value.middle_name = user.value.middle_name || ''
        form.value.last_name = user.value.last_name || ''
        form.value.email = user.value.email || ''
        form.value.phone = user.value.phone || ''
        form.value.address = user.value.address || ''
        form.value.city = user.value.city || ''
        form.value.zip_code = user.value.zip_code || ''
        form.value.is_superadmin = user.value.is_superadmin || false
        form.value.is_active = user.value.is_active || true
        form.value.organization_id = user.value.organization_id || ''
        // Set the role if it exists
        form.value.role_id = user.value.role ? user.value.role.id : ''
      } catch (error) {
        console.error('Error fetching user:', error)
        alert('Failed to load user data. Redirecting back to users list.')
        router.push('/users')
      } finally {
        loading.value = false
      }
    }

    const fetchRoles = async () => {
      rolesLoading.value = true
      try {
        const response = await axios.get('/api/roles')
        roles.value = response.data || []

        // If we have role_id but the role doesn't exist in the list, reset it
        if (form.value.role_id && !roles.value.some(role => role.id === form.value.role_id)) {
          form.value.role_id = ''
        }

        // If only one role is available and no role selected, auto-select it
        if (roles.value.length === 1 && !form.value.role_id) {
          form.value.role_id = roles.value[0].id
        }
      } catch (error) {
        console.error('Error fetching roles:', error)
      } finally {
        rolesLoading.value = false
      }
    }

    const updateUser = async () => {
      saving.value = true
      errors.value = {}

      try {
        // Create a copy of the form data for submission
        const userData = { ...form.value }

        await axios.put(`/api/users/${userId}`, userData)
        router.push({
          path: '/users',
          query: { success: `User ${form.value.first_name} ${form.value.last_name} updated successfully` }
        })
      } catch (error) {
        console.error('Error updating user:', error)
        if (error.response && error.response.data.errors) {
          errors.value = error.response.data.errors
        } else {
          alert('Failed to update user. Please try again.')
        }
      } finally {
        saving.value = false
      }
    }

    onMounted(() => {
      fetchUser()

      fetchRoles()
    })

    return {
      form,
      user,

      roles,
      rolesLoading,
      loading,
      saving,
      errors,
      updateUser,
      selectedRole
    }
  }
}
</script>

<style scoped>
.user-edit-page {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.card {
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  border-radius: 6px;
  overflow: hidden;
}

.card-header {
  border-bottom: none;
}

.card-header-title {
  font-weight: 600;
}

.select select:focus,
.input:focus,
.textarea:focus {
  border-color: var(--primary);
  box-shadow: 0 0 0 0.125em rgba(0, 209, 178, 0.25);
}

.checkbox:hover {
  color: var(--primary);
}

.checkbox input {
  margin-right: 0.5rem;
}

.button {
  transition: all 0.2s ease;
}

.button:hover {
  transform: translateY(-2px);
}
</style>
